import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, AlertTriangle, CheckCircle2, AlertCircle, Check, Pencil } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../Components/StakingRisk/ui/select";
import { DeleteAssessmentbyIdThunk, UpdateAssessmentByIdThunk } from "../../../Store/Thunks/StakingRiskThunk";
import { Textarea } from "../../../Components/StakingRisk/ui/textarea";
import { Button } from "../../../Components/StakingRisk/ui/button";
import { cn } from "../../../StakingRisk/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";


export default function ParameterRow({
  id,
  label,
  options,
  value,
  comment,
  isCustom = false,
  onChange,
  delay = 500,
}) {
  const dispatch = useDispatch();
  
  const [localValue, setLocalValue] = useState(value);
  const [localComment, setLocalComment] = useState(comment);
  const [paramToDelete, setParamToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false); 
  const [editedName, setEditedName] = useState();

  const assessmentupdate = useSelector((state) => state.StakingRiskReducer.assessmentupdate);

useEffect(() => {
  if (!isEditing && label) {
    setEditedName(label);
  }
}, [label, isEditing]);

  useEffect(() => {
    setLocalValue(value);
    setLocalComment(comment);
  }, [value, comment]);

  useEffect(() => {
    if (localValue === value && localComment === comment) return;
    const htmlComment = localComment
      ? localComment.replace(/\r?\n/g, "<br />")
      : "";
    const timer = setTimeout(() => {
      onChange(localValue, htmlComment);
      setIsSaving(true);
      setTimeout(() => {
        setIsSaving(false);
     
      }, 2000);
    }, delay);

    return () => clearTimeout(timer);
  }, [localValue, localComment, onChange, delay, value, comment,assessmentupdate]);

  const getStatusIndicator = () => {
    if (isSaving) {     
            return <span className="text-xs text-green-500">Saved ✔</span>;
    }
    return null;
  };

const getValueColor = (optionValue, label) => {
  const highRiskValues = [
    "High", "Immature", "Unreliable", "Negative", "Lawsuits Exist",
    "AML Activities", "Non-Compliant", "Limited", "Centralized"
  ];
  const mediumRiskValues = ["Medium", "Anonymous", "Standard", "Not FI","Moderate Compliance"];

  if (label === "Uptime" && optionValue === "High") {
    return "text-green-600";
  }

  if (highRiskValues.includes(optionValue)) return "text-red-600";
  if (mediumRiskValues.includes(optionValue)) return "text-yellow-600";
  return "text-green-600";
};

const getValueIcon = (optionValue, label) => {
  const highRiskValues = [
    "High", "Immature", "Unreliable", "Negative", "Lawsuits Exist",
    "AML Activities", "Non-Compliant", "Limited", "Centralized"
  ];
  const mediumRiskValues = ["Medium", "Anonymous", "Standard", "Not FI","Moderate Compliance"];

  if (label === "Uptime" && optionValue === "High") {
    return <CheckCircle2 className="w-4 h-4 mr-1 text-green-600" />;
  }

  if (highRiskValues.includes(optionValue)) return <AlertTriangle className="w-4 h-4 mr-1 text-red-600" />;
  if (mediumRiskValues.includes(optionValue)) return <AlertCircle className="w-4 h-4 mr-1 text-yellow-600" />;
  return <CheckCircle2 className="w-4 h-4 mr-1 text-green-600" />;
};
  const handleDeleteConfirm = () => {
    dispatch(DeleteAssessmentbyIdThunk(paramToDelete));
    setParamToDelete(null);
  };

const handleTitleSave = () => {
  if (!editedName.trim()) return;

   dispatch(UpdateAssessmentByIdThunk(id, value, comment, editedName))
  setIsEditing(false);
};

const handleCancelEdit = () => {
  setEditedName(label);
  setIsEditing(false);
};
  return (
    <>
      <div className="group py-3 px-4 border-b last:border-0 border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
        <div className="grid grid-cols-12 gap-4 items-start">
          <div className="col-span-12 md:col-span-3 flex justify-between items-center">
            <div className="flex items-center gap-2">                
                  {isEditing ? (
                      <>
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleTitleSave();
                            if (e.key === "Escape") handleCancelEdit();
                          }}
                          autoFocus
                          className="text-1xs md:text-1xs font-medium bg-transparent border-b border-gray-400 focus:outline-none focus:border-blue-600 dark:text-white"
                        />
                        <button onClick={handleTitleSave} title="Save" className="text-green-600 hover:text-green-800">
                          <Check className="h-5 w-5" />
                        </button>
                        <button onClick={handleCancelEdit} title="Cancel" className="text-gray-500 hover:text-gray-700">
                          <X className="h-5 w-5" />
                        </button>
                      </>
                      ) : (
                      <>
                        <span className="font-medium text-gray-900 dark:text-gray-200 flex items-center gap-2"   onClick={() => setIsEditing(true)}
        title="Click to edit">{editedName}
                           <Pencil className="h-3 w-3" />
                        </span>
                      </>
                      )}                          
              {getStatusIndicator()}
            </div>
            {isCustom && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7"
                    onClick={() => setParamToDelete(id)}
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove the parameter <span className="font-semibold">"{label}"</span> from this assessment tab.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setParamToDelete(null)}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteConfirm}
                      className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>

          <div className="col-span-12 md:col-span-3">
            <Select value={localValue || ''} onValueChange={setLocalValue}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select...">
                  {localValue ? (
                    <div className="flex items-center">
                      {getValueIcon(localValue,label)}
                      <span className={getValueColor(localValue,label)}>
                        {localValue}
                      </span>
                    </div>
                  ) : (
                    "Select..."
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                 <SelectItem
                  key="empty"
                  value={null}
                 className="flex items-center"
                >
                  Select...
                </SelectItem>
                {options.map((option) => (
                  <SelectItem
                    key={option}
                    value={option}
                    className={cn("flex items-center", getValueColor(option,label))}
                  >
                    {getValueIcon(option,label)}
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-12 md:col-span-6">
            <Textarea
              placeholder="Add your comments here..."
              className="min-h-[80px] resize-y whitespace-pre-wrap"           
              value={localComment?.replace(/<br\s*\/?>/gi, "\n") || ''}
              onChange={(e) => setLocalComment(e.target.value.replace(/\r?\n/g, "\n"))}               
            />
          </div>
        </div>
      </div>
    </>
  );
}